/**
 * InteractionTimeline — the "when did I last talk to this realtor" widget.
 * Lives on the RealtorEditDialog. Shows every logged Call / SMS / Email /
 * DM / Note in reverse-chronological order and lets the user add or
 * delete entries.
 *
 * Kept intentionally small: the goal is a single-tap "just called them"
 * log, NOT a full communications inbox. Free-text note is optional so
 * "click Call" → row appears with no typing is the happy path.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Phone,
  MessageSquare,
  Mail,
  MessageCircle,
  StickyNote,
  Handshake,
  Plus,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from "lucide-react";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/components/outreach/utils";

const TYPES = [
  { key: "call", label: "Call", icon: Phone, color: "text-emerald-600" },
  { key: "sms", label: "SMS", icon: MessageSquare, color: "text-blue-600" },
  { key: "email", label: "Email", icon: Mail, color: "text-indigo-600" },
  { key: "dm", label: "DM", icon: MessageCircle, color: "text-pink-600" },
  { key: "note", label: "Note", icon: StickyNote, color: "text-amber-600" },
  { key: "referral", label: "Referral", icon: Handshake, color: "text-teal-600" },
];

const TYPE_MAP = Object.fromEntries(TYPES.map((t) => [t.key, t]));

function daysSince(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function formatDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function InteractionTimeline({ realtor, onCountChange, onInteractionChange, flushDirtyEdits }) {
  const { user } = useAuth();
  const currency = user?.currency === "USD" ? "USD" : "CAD";
  const realtorId = realtor?.id;
  const realtorName = realtor?.name || `${realtor?.first_name || ""} ${realtor?.last_name || ""}`.trim();
  const realtorPhone = realtor?.phone || "";
  const realtorEmail = realtor?.email || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [posting, setPosting] = useState(false);
  const [pendingType, setPendingType] = useState(null); // which type is being added right now
  const [note, setNote] = useState("");
  // Only show "logged by X" chips when 2+ users share the realtor's
  // location. For single-user locations the attribution is noise. Passed
  // through from the parent when available (edit dialog re-fetches on
  // save), otherwise we fetch once on mount so single-user profiles
  // stay clean without a network round-trip surprise.
  const [showActors, setShowActors] = useState(
    typeof realtor?.location_user_count === "number"
      ? realtor.location_user_count > 1
      : false,
  );

  useEffect(() => {
    // If the parent already resolved the count, keep it. Otherwise fetch.
    if (typeof realtor?.location_user_count === "number") {
      setShowActors(realtor.location_user_count > 1);
      return;
    }
    if (!realtorId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/realtors/${realtorId}`);
        if (!cancelled) {
          setShowActors((data?.location_user_count || 0) > 1);
        }
      } catch (_) { /* silent — attribution just stays hidden */ }
    })();
    return () => { cancelled = true; };
  }, [realtorId, realtor?.location_user_count]);

  // After a timeline change, fetch the fresh realtor doc so parent lists
  // (Outreach queue, Realtors list) can patch the row in place — no full
  // page reload. Silent on failure: the timeline itself already updated
  // its own state so the dialog stays consistent.
  const notifyRealtorChanged = useCallback(async () => {
    if (!realtorId || !onInteractionChange) return;
    try {
      const { data } = await api.get(`/realtors/${realtorId}`);
      if (data) onInteractionChange(data);
    } catch (_) { /* silent */ }
  }, [realtorId, onInteractionChange]);

  const load = useCallback(async () => {
    if (!realtorId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/realtors/${realtorId}/interactions`);
      setItems(Array.isArray(data) ? data : []);
      onCountChange?.(Array.isArray(data) ? data.length : 0);
    } catch (e) {
      toast.error("Couldn't load timeline", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [realtorId, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  // Per-inspector referral breakdown — YOUR totals highlighted first, the
  // whole team's counts + amounts shown too (admins see everyone).
  const loadBreakdown = useCallback(async () => {
    if (!realtorId) return;
    try {
      const { data } = await api.get(`/realtors/${realtorId}/referral-breakdown`);
      setBreakdown(data);
    } catch (_) { /* silent — breakdown is a nice-to-have */ }
  }, [realtorId]);

  useEffect(() => {
    loadBreakdown();
  }, [loadBreakdown]);

  // Auto-log an interaction. Kept single-tap: pass the type + an optional
  // `deepLink` (tel: / sms: / mailto:) and we both open the link AND
  // write the timeline row. If the link is provided we open it BEFORE
  // the POST so mobile browsers don't block the popup.
  const quickLog = async (type, deepLink) => {
    if (!realtorId || posting) return;
    if (deepLink && typeof window !== "undefined") {
      // Use window.location to preserve OS handoff on iOS/Android — a
      // synthetic <a>.click() sometimes gets swallowed by Chromium.
      window.location.href = deepLink;
    }
    setPosting(true);
    setPendingType(type);
    try {
      // Auto-save any pending profile edits (notes/tags/phone/...) before
      // logging the timeline event, so the user doesn't lose them when
      // they tap Call / SMS / Email / DM / Note / Referral.
      if (flushDirtyEdits) await flushDirtyEdits();
      const { data } = await api.post(`/realtors/${realtorId}/interactions`, {
        type,
        note: note.trim() || undefined,
      });
      setItems((prev) => [data, ...prev]);
      onCountChange?.(items.length + 1);
      setNote("");
      toast.success(`Logged ${TYPE_MAP[type]?.label || type}`, {
        description: realtorName ? `${realtorName} · ${daysSince(data.occurred_at) || "just now"}` : undefined,
        duration: 3500,
      });
      notifyRealtorChanged();
    } catch (e) {
      toast.error("Couldn't log interaction", { description: formatApiError(e) });
    } finally {
      setPosting(false);
      setPendingType(null);
    }
  };

  const removeItem = async (id) => {
    if (!realtorId) return;
    try {
      await api.delete(`/realtors/${realtorId}/interactions/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      onCountChange?.(items.length - 1);
      notifyRealtorChanged();
      loadBreakdown();
    } catch (e) {
      toast.error("Couldn't remove", { description: formatApiError(e) });
    }
  };

  // Referral form state — separate from quickLog because we need
  // client name / inspection date / fee. Opens when the Referral button
  // is tapped; collapses on submit.
  const [refOpen, setRefOpen] = useState(false);
  const [refClient, setRefClient] = useState("");
  const [refDate, setRefDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [refFee, setRefFee] = useState("");
  const submitReferral = async () => {
    if (!realtorId || posting) return;
    const clientName = refClient.trim();
    if (!clientName) {
      toast.error("Client name is required");
      return;
    }
    setPosting(true);
    setPendingType("referral");
    try {
      if (flushDirtyEdits) await flushDirtyEdits();
      const feeNum = parseFloat(refFee);
      const { data } = await api.post(`/realtors/${realtorId}/interactions`, {
        type: "referral",
        referral_client: clientName,
        referral_inspection_date: refDate || undefined,
        referral_fee: Number.isFinite(feeNum) && feeNum >= 0 ? feeNum : undefined,
        note: note.trim() || undefined,
      });
      setItems((prev) => [data, ...prev]);
      onCountChange?.(items.length + 1);
      setNote("");
      setRefClient("");
      setRefFee("");
      setRefDate(new Date().toISOString().slice(0, 10));
      setRefOpen(false);
      loadBreakdown();
      toast.success("Referral logged", {
        description: `${clientName}${refFee ? ` · $${refFee}` : ""}`,
        duration: 3500,
      });
      notifyRealtorChanged();
    } catch (e) {
      toast.error("Couldn't log referral", { description: formatApiError(e) });
    } finally {
      setPosting(false);
      setPendingType(null);
    }
  };

  const lastInteraction = useMemo(() => items[0] || null, [items]);

  return (
    <div
      className="rounded-md border border-slate-200 bg-slate-50/40 mt-4"
      data-testid="interaction-timeline"
    >
      <div className="px-3 py-2 border-b border-slate-200 flex flex-wrap items-center gap-2">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <Phone className="h-3.5 w-3.5 text-slate-500" />
          Timeline
        </div>
        <div className="text-xs text-slate-500">
          {items.length
            ? `${items.length} interaction${items.length === 1 ? "" : "s"}${lastInteraction ? ` · last ${daysSince(lastInteraction.occurred_at) || "recent"}` : ""}`
            : "No interactions yet — log your first below"}
        </div>
        {breakdown && breakdown.team.count > 0 ? (
          <div className="w-full text-[11px] mt-0.5 flex flex-wrap items-center gap-x-1.5" data-testid="referral-breakdown">
            <span className="inline-flex items-center gap-1 rounded bg-teal-50 text-teal-800 border border-teal-200 px-1.5 py-0.5 font-semibold">
              You: {breakdown.mine.count} · {formatCurrency(breakdown.mine.revenue, currency)}
            </span>
            <span className="text-slate-500">
              Team: {breakdown.team.count} referral{breakdown.team.count === 1 ? "" : "s"} · {formatCurrency(breakdown.team.revenue, currency)}
            </span>
            {breakdown.by_user.length > 1 ? (
              <span className="text-slate-400" title={breakdown.by_user.map((u) => `${u.name}: ${u.count} · ${formatCurrency(u.revenue, currency)}`).join("\n")}>
                ({breakdown.by_user.length} inspectors)
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Quick-log row — tap a type to log it. tel:/sms:/mailto: deep-link
          also fires so the user's phone dialer / SMS app / mail app opens
          in the same tap. Note is optional and applies to whichever type
          you tap next. */}
      <div className="px-3 py-2.5 border-b border-slate-200 space-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <QuickBtn
            type="call"
            disabled={posting}
            active={pendingType === "call"}
            onClick={() => quickLog("call", realtorPhone ? `tel:${realtorPhone}` : null)}
            hint={realtorPhone ? `Call ${realtorPhone}` : "No phone — logs a call anyway"}
            testid="log-call-btn"
          />
          <QuickBtn
            type="sms"
            disabled={posting}
            active={pendingType === "sms"}
            onClick={() => quickLog("sms", realtorPhone ? `sms:${realtorPhone}` : null)}
            hint={realtorPhone ? `Text ${realtorPhone}` : "No phone — logs a text anyway"}
            testid="log-sms-btn"
          />
          <QuickBtn
            type="email"
            disabled={posting}
            active={pendingType === "email"}
            onClick={() => quickLog("email", realtorEmail ? `mailto:${realtorEmail}` : null)}
            hint={realtorEmail ? `Email ${realtorEmail}` : "No email — logs one anyway"}
            testid="log-email-btn"
          />
          <QuickBtn
            type="dm"
            disabled={posting}
            active={pendingType === "dm"}
            onClick={() => quickLog("dm")}
            hint="Log a social DM"
            testid="log-dm-btn"
          />
          <QuickBtn
            type="note"
            disabled={posting}
            active={pendingType === "note"}
            onClick={() => quickLog("note")}
            hint="Add a plain note"
            testid="log-note-btn"
          />
          <QuickBtn
            type="referral"
            disabled={posting}
            active={pendingType === "referral"}
            onClick={() => setRefOpen((o) => !o)}
            hint="Log an inspection referral this realtor sent you"
            testid="log-referral-btn"
          />
        </div>
        {/* Referral form — appears when the Referral button is tapped.
            Client name is required; date defaults to today; fee is
            optional but recommended for lifetime-revenue-per-realtor
            math later. */}
        {refOpen ? (
          <div
            className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-200"
            data-testid="referral-form"
          >
            <div className="flex-1 min-w-[180px]">
              <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                Client name
              </label>
              <Input
                value={refClient}
                onChange={(e) => setRefClient(e.target.value)}
                placeholder="e.g. Bob Smith"
                className="min-h-[36px] text-sm mt-0.5"
                data-testid="referral-client-input"
              />
            </div>
            <div className="min-w-[140px]">
              <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                Inspection date
              </label>
              <Input
                type="date"
                value={refDate}
                onChange={(e) => setRefDate(e.target.value)}
                className="min-h-[36px] text-sm mt-0.5"
                data-testid="referral-date-input"
              />
            </div>
            <div className="min-w-[120px]">
              <label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
                Fee ($)
              </label>
              <Input
                type="number"
                min="0"
                step="1"
                value={refFee}
                onChange={(e) => setRefFee(e.target.value)}
                placeholder="650"
                className="min-h-[36px] text-sm mt-0.5"
                data-testid="referral-fee-input"
              />
            </div>
            <Button
              onClick={submitReferral}
              disabled={posting || !refClient.trim()}
              size="sm"
              className="min-h-[36px] bg-teal-600 hover:bg-teal-700 text-white shrink-0"
              data-testid="referral-submit-btn"
            >
              {posting && pendingType === "referral" ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : (
                <Handshake className="h-3.5 w-3.5 mr-1" />
              )}
              Log referral
            </Button>
          </div>
        ) : null}
        <div className="flex items-center gap-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note — e.g. 'discussed spring listings'"
            className="min-h-[38px] text-sm"
            data-testid="interaction-note-input"
            onKeyDown={(e) => {
              // ⌘/Ctrl-Enter quickly logs a note without picking a type.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && note.trim()) {
                quickLog("note");
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 min-h-[38px]"
            onClick={() => quickLog("note")}
            disabled={posting || !note.trim()}
            title="Add a note (⌘/Ctrl+Enter)"
            data-testid="add-note-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Note
          </Button>
        </div>
      </div>

      {/* Timeline itself */}
      <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
        {loading ? (
          <div className="px-3 py-3 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-3 text-sm text-slate-400 italic">
            No interactions logged yet.
          </div>
        ) : (
          items.map((it) => (
            <TimelineRow
              key={it.id}
              item={it}
              currency={currency}
              onDelete={() => removeItem(it.id)}
              showActor={showActors}
            />
          ))
        )}
      </div>
    </div>
  );
}

function QuickBtn({ type, onClick, disabled, active, hint, testid }) {
  const t = TYPE_MAP[type];
  if (!t) return null;
  const Icon = t.icon;
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={hint}
      className="h-8 gap-1.5"
      data-testid={testid}
    >
      {active ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icon className={`h-3.5 w-3.5 ${t.color}`} />
      )}
      <span className="text-xs font-medium">{t.label}</span>
    </Button>
  );
}

function TimelineRow({ item, onDelete, showActor = false, currency = "CAD" }) {
  const t = TYPE_MAP[item.type] || TYPE_MAP.note;
  const Icon = t.icon;
  const when = daysSince(item.occurred_at || item.created_at);
  const dir = item.direction || "outgoing";
  const DirIcon = dir === "outgoing" ? ArrowRight : ArrowLeft;
  const dirTitle = dir === "outgoing" ? "You reached out" : "They reached out";
  // Attribution chip — only rendered when the realtor's location has
  // 2+ users assigned, so single-operator setups don't get noisy.
  const actor = item.actor_name || item.actor_email || "";
  return (
    <div
      className="px-3 py-2 flex items-start gap-2 text-sm hover:bg-white"
      data-testid={`timeline-item-${item.id}`}
    >
      <div className={`shrink-0 mt-0.5 h-6 w-6 rounded-full bg-white border border-slate-200 flex items-center justify-center ${t.color}`}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-slate-800 font-medium">
          {t.label}
          <DirIcon className="h-3 w-3 text-slate-400" title={dirTitle} />
          {item.platform ? (
            <span className="text-[10px] uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
              {item.platform}
            </span>
          ) : null}
          {showActor && actor ? (
            <span
              className="text-[10px] font-medium text-slate-500 bg-slate-100 rounded px-1.5 py-0.5"
              title={item.actor_email || ""}
              data-testid={`timeline-item-actor-${item.id}`}
            >
              by {actor}
            </span>
          ) : null}
          <span className="text-xs font-normal text-slate-500 ml-auto shrink-0">{when}</span>
        </div>
        {/* Referral-specific detail line: "Bob Smith · $650 · Feb 28" */}
        {item.type === "referral" && (item.referral_client || item.referral_fee || item.referral_inspection_date) ? (
          <div className="text-xs text-teal-700 mt-0.5 font-medium">
            {item.referral_client || "Unnamed client"}
            {item.referral_fee ? ` · ${formatCurrency(Number(item.referral_fee), currency)}` : ""}
            {item.referral_inspection_date ? ` · ${formatDateShort(item.referral_inspection_date)}` : ""}
          </div>
        ) : null}
        {item.note ? (
          <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap break-words">
            {item.note}
          </div>
        ) : null}
      </div>
      <button
        onClick={onDelete}
        className="shrink-0 text-slate-300 hover:text-rose-600 transition-colors"
        aria-label="Delete interaction"
        title="Delete this entry"
        data-testid={`timeline-item-delete-${item.id}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
